"use client";

import type { IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import { Button, ScreenOverlay, useMediaQuery, useModal } from "@tosize/ui";
import { AnimatePresence, motion } from "motion/react";
import { usePathname } from "next/navigation";
import { AlertDialog } from "radix-ui";
import { type ReactNode, useCallback, useEffect } from "react";

type CookieState = "denied" | "granted";

interface CookieConsentProps {
	cookiePagePath: string;
	children: ReactNode;
	title: string;
	cancelBtn: { icon?: IconDefinition; name: string };
	actionBtn: { icon?: IconDefinition; name: string };
}

export const LOCALSTORAGE_COOKIE_KEY = "cookieConsent";

export default function CookieConsent(props: CookieConsentProps) {
	const { cookiePagePath, children, title, actionBtn, cancelBtn } = props;
	const pathName = usePathname();

	const localCookie =
		typeof window === "undefined" ? false : localStorage.getItem(LOCALSTORAGE_COOKIE_KEY);
	const defaultOpen =
		localCookie !== "granted" && localCookie !== "denied" && pathName !== cookiePagePath;

	const dialog = useModal(defaultOpen);
	const isSmallDevice = useMediaQuery({ max: "sm" });

	if (typeof window !== "undefined" && window.gtag) {
		// Set default consent state
		window.gtag("consent", "default", {
			ad_storage: "denied",
			ad_personalization: "denied",
			ad_user_data: "denied",
			analytics_storage: "granted",
			functionality_storage: "granted",
			security_storage: "granted",
		});
	}

	const setConsent = useCallback((consent: CookieState) => {
		localStorage.setItem(LOCALSTORAGE_COOKIE_KEY, consent);
		if (window.gtag) {
			window.gtag("consent", "update", {
				ad_storage: consent,
				ad_personalization: consent,
				ad_user_data: consent,
				personalization_storage: consent,
			});
		}
	}, []);

	if (localCookie === "granted" || localCookie === "denied") {
		setConsent(localCookie as CookieState);
	}

	useEffect(() => {
		function onClick() {
			const element = document.querySelector('[data-id="cookieConsent"]');

			if (dialog.open && !element?.checkVisibility()) {
				setConsent("denied");
				dialog.close();
			}
		}

		window.addEventListener("click", onClick);

		return () => {
			window.removeEventListener("click", onClick);
		};
	}, [dialog, setConsent]);

	return (
		<AlertDialog.Root {...dialog.props}>
			<AnimatePresence>
				{dialog.open && (
					<AlertDialog.Portal forceMount>
						<AlertDialog.Overlay forceMount>
							<ScreenOverlay variant="animated" />
						</AlertDialog.Overlay>
						<AlertDialog.Content forceMount onEscapeKeyDown={(e) => e.preventDefault()}>
							<motion.div
								data-id="cookieConsent"
								className="fixed bottom-[env(safe-area-inset-bottom,0px)] left-1/2 z-dialog w-full max-w-full rounded-t bg-white p-4 shadow-lg sm:top-1/2 sm:bottom-auto sm:w-[calc(100%-2rem)] sm:max-w-xl sm:rounded-b"
								initial={{
									opacity: 1,
									scale: isSmallDevice ? 1 : 0.9,
									y: isSmallDevice ? "100%" : "-50%",
									x: "-50%",
								}}
								exit={{
									opacity: 0,
									scale: isSmallDevice ? 1 : 0.9,
									y: isSmallDevice ? "100%" : "-50%",
									x: "-50%",
								}}
								animate={{
									opacity: 1,
									scale: 1,
									y: isSmallDevice ? "0%" : "-50%",
									x: "-50%",
								}}
								transition={{ duration: 0.15 }}
							>
								<div className="mb-4 flex flex-column items-baseline">
									<div>
										{!!title && (
											<AlertDialog.Title className="font-bold font-primary text-lg">
												{title}
											</AlertDialog.Title>
										)}
									</div>
								</div>

								<AlertDialog.Description asChild>
									<div>{children}</div>
								</AlertDialog.Description>

								<div className="mt-4 flex justify-end space-x-4">
									<AlertDialog.Cancel asChild>
										<Button
											onClick={() => {
												setConsent("denied");
												dialog.close();
											}}
											starticon={cancelBtn.icon}
											variant="link"
										>
											{cancelBtn.name}
										</Button>
									</AlertDialog.Cancel>

									<AlertDialog.Action asChild>
										<Button
											color="success"
											onClick={() => {
												setConsent("granted");
												dialog.close();
											}}
											starticon={actionBtn.icon}
											autoFocus
										>
											{actionBtn.name}
										</Button>
									</AlertDialog.Action>
								</div>
							</motion.div>
						</AlertDialog.Content>
					</AlertDialog.Portal>
				)}
			</AnimatePresence>
		</AlertDialog.Root>
	);
}
